import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useI18n, LANGS } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LayoutDashboard, Users, CalendarDays, Wallet, FileText, Settings, LogOut, Building2, CalendarClock, Inbox, ExternalLink, KeyRound, Globe, BookOpen } from "lucide-react";

const navItems = [
  { to: "/admin", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/tenants", key: "tenants", icon: Users },
  { to: "/admin/reservations", key: "reservations", icon: CalendarDays },
  { to: "/admin/dues", key: "dues", icon: CalendarClock },
  { to: "/admin/inquiries", key: "inquiries", icon: Inbox },
  { to: "/admin/finances", key: "finances", icon: Wallet, roles: ["admin", "staff"] },
  { to: "/admin/documents", key: "documents", icon: FileText },
  { to: "/admin/content", key: "content", icon: Globe, roles: ["admin"] },
  { to: "/admin/settings", key: "settings", icon: Settings, roles: ["admin"] },
  { to: "/admin/guide", key: "guide", icon: BookOpen },
];

export const Layout = ({ children }) => {
  const { t, lang, changeLang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const items = navItems.filter((i) => !i.roles || i.roles.includes(user?.role));

  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const submitPw = async () => {
    if (pw.next.length < 8) { toast.error(t("account.tooShort")); return; }
    if (pw.next !== pw.confirm) { toast.error(t("account.mismatch")); return; }
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success(t("account.success"));
      setPwOpen(false); setPw({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setPwLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-linen font-sans">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-sand border-r border-border fixed h-screen">
        <div className="px-6 py-6 border-b border-border/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-teak flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-semibold text-teak leading-tight">{t("appName")}</div>
              <div className="text-xs text-muted-foreground">{t("appSub")}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                  isActive ? "bg-teak text-white font-medium" : "text-foreground/70 hover:bg-white/70 hover:text-teak"
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px]" />
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border/60">
          <div className="px-3 py-2 mb-1">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <a href="/" target="_blank" rel="noreferrer" data-testid="view-site-link"
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground/70 hover:bg-white/70 hover:text-teak transition-colors duration-200">
            <ExternalLink className="w-[18px] h-[18px]" /> {t("site.viewSite")}
          </a>
          <button onClick={() => setPwOpen(true)} data-testid="change-pw-btn"
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground/70 hover:bg-white/70 hover:text-teak transition-colors duration-200">
            <KeyRound className="w-[18px] h-[18px]" /> {t("account.changePw")}
          </button>
          <button onClick={handleLogout} data-testid="logout-btn"
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground/70 hover:bg-white/70 hover:text-destructive transition-colors duration-200">
            <LogOut className="w-[18px] h-[18px]" /> {t("common.logout")}
          </button>
          <Dialog open={pwOpen} onOpenChange={setPwOpen}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>{t("account.changePw")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>{t("account.current")}</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" /></div>
                <div className="space-y-2"><Label>{t("account.new")}</Label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-new" /></div>
                <div className="space-y-2"><Label>{t("account.confirm")}</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPwOpen(false)}>{t("common.cancel")}</Button>
                <Button onClick={submitPw} disabled={pwLoading} className="bg-teak hover:bg-teak-hover text-white" data-testid="pw-save">{t("common.save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-linen/95 backdrop-blur border-b border-border px-5 md:px-8 py-3 flex items-center justify-between">
          <div className="md:hidden flex items-center gap-2 font-semibold text-teak">
            <Building2 className="w-5 h-5" /> {t("appName")}
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-1 bg-white rounded-full border border-border p-1" data-testid="lang-switcher">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => changeLang(l.code)}
                data-testid={`lang-${l.code}`}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${
                  lang === l.code ? "bg-teak text-white" : "text-muted-foreground hover:text-teak"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </header>

        {/* Mobile nav */}
        <nav className="md:hidden flex overflow-x-auto gap-1 px-3 py-2 bg-sand border-b border-border">
          {items.map((item) => (
            <NavLink key={item.key} to={item.to} end={item.end} data-testid={`mnav-${item.key}`}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-md text-[11px] whitespace-nowrap ${isActive ? "bg-teak text-white" : "text-foreground/70"}`}>
              <item.icon className="w-4 h-4" />
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 px-5 md:px-8 py-6 animate-fade-up">{children}</main>
      </div>
    </div>
  );
};
